import { DateTimeRange } from '@modules/common/components/DateRangePicker/types';
import { replaceDateObjectWithTimeStamp } from '@modules/common/utils';
import { getHasGlobalFilterDurationChangedFromDefault } from '@modules/dashboard/helpers/getHasGlobalFilterDurationChangedFromDefault';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import isEqual from 'lodash/isEqual';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { shallow } from 'zustand/shallow';

export type GlobalFilterQueryParameter =
  | 'dashboardDuration'
  | 'dashboardWhereClause';

export const useGenerateGlobalFilterSearchParamsFromStore = () => {
  const { globalFilterDuration, globalFilterWhereClause } = useDashboardStore(
    ({ query }) => {
      return {
        globalFilterDuration: query.duration,
        globalFilterWhereClause: query.whereClause,
      };
    },
    shallow,
  );

  const hasGlobalFilterDurationChangedFromDefault =
    getHasGlobalFilterDurationChangedFromDefault(globalFilterDuration);

  const shouldUseGlobalFilterWhereClause = !isEqual(
    globalFilterWhereClause,
    EmptyWhereClause,
  );

  const shouldUseGlobalFilter =
    hasGlobalFilterDurationChangedFromDefault ||
    shouldUseGlobalFilterWhereClause;

  if (!shouldUseGlobalFilter) {
    return '';
  }

  const stringifiedDuration = JSON.stringify(
    replaceDateObjectWithTimeStamp(globalFilterDuration as DateTimeRange),
  );
  const stringifiedWhereClause = JSON.stringify(globalFilterWhereClause);

  const mappedQueryParamsToValue: Partial<
    Record<GlobalFilterQueryParameter, string | undefined>
  > = {};

  if (hasGlobalFilterDurationChangedFromDefault) {
    mappedQueryParamsToValue['dashboardDuration'] = stringifiedDuration;
  }

  if (shouldUseGlobalFilterWhereClause) {
    mappedQueryParamsToValue['dashboardWhereClause'] = stringifiedWhereClause;
  }

  const globalFilterSearchParams = new URLSearchParams();

  Object.entries(mappedQueryParamsToValue).forEach(([queryParam, value]) => {
    if (value) {
      globalFilterSearchParams.set(queryParam, value);
    }
  });

  return globalFilterSearchParams.toString();
};
